/**
 *  [2021] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */


/**
 * :ZNOTE:
 * tag: cacheManage
 * note:
 *  - CacheManageクラス内で利用するメモリ内データ管理機構
 *  - 処理高速化のために導入した
 * :ZNOTE:
 */

/**
 * アーカイブデータに対応する
 *
 * アーカイブデータは、サーバ上ではgzip圧縮したものをS3で保管され、
 * IndexedDBでは複数レコードをarrayにして１レコード内に保管される
 * （__archivedEntriesプロパティ）
 *
 * 一方inMemoryCache上では、各レコードを個別に保管することで、
 * アプリからはアーカイブかどうかに関係なくデータを利用できるようにする。
 */


class InMemoryCache {
  protected readonly secondaryKeyName: string;
  protected readonly getFunc: (string) => any;
  protected dataCache: any = {};  // {primaryKey: {secondaryKey: data}} の形なので、同じsecondaryKeyの情報は後から上書きされる
  protected dataCacheAccessTime: any = {};

  /**
   * コンストラクタ
   * @param secondaryKeyName {string}
   * @param getFunc {(string) => any}
   */
  constructor(secondaryKeyName: string, getFunc: (string) => any) {
    this.secondaryKeyName = secondaryKeyName;
    this.getFunc = getFunc;
  }

  public clear = () => {
    this.dataCache = {};
    this.dataCacheAccessTime = {};
  };

  /**
   * this.dataCacheを更新する。
   * @param primaryKeyValue {string}
   * @param data {any}
   * @protected
   */
  public async updateInMemory(primaryKeyValue: string, data?: any) {
    // this.dataCacheのデータを更新する。データがなければIndexedDbからデータを読み込む
    if (!this.dataCache[primaryKeyValue]) {
      await this.loadInMemory(primaryKeyValue);
    }

    if (Array.isArray(data)) {
      data.forEach((ent: any) => this._putInMemory(primaryKeyValue, ent));
    } else {
      this._putInMemory(primaryKeyValue, data);
    }

    this.dataCacheAccessTime[primaryKeyValue] = new Date().getTime();
  }

  /**
   * this.dataCacheにIndexedDbからデータを読み込んでくる
   * @param primaryKeyValue {string}
   * @protected
   */
  public async loadInMemory(primaryKeyValue: string) {
    if (this.dataCache[primaryKeyValue]) return;

    // EXP: 管理するprimaryKeyの数が多くなったら古いものを削除する(最大10件)
    if (Object.keys(this.dataCache).length >= 10 && Object.keys(this.dataCacheAccessTime).length > 0) {
      const minKey = Object.keys(this.dataCacheAccessTime).reduce((acc: string, d: string) => this.dataCacheAccessTime[acc] > this.dataCacheAccessTime[d] ? d : acc);
      delete this.dataCache[minKey];
      delete this.dataCacheAccessTime[minKey];
    }

    // EXP: this.dataCache[primaryKeyValue]にデータを読み込む
    this.dataCache[primaryKeyValue] = {};
    let allEntries = await this.getFunc(primaryKeyValue);
    if (!allEntries || allEntries.length === 0) return;
    allEntries = allEntries.sort((a: any, b: any) => a.updatedAt - b.updatedAt);  // 必ずupdatedAt昇順で並べる必要がある（古いものからメモリに展開していき、新しいもので上書きする）
    allEntries.forEach((ent: any) => this._putInMemory(primaryKeyValue, ent));
    this.dataCacheAccessTime[primaryKeyValue] = new Date().getTime();
  }

  private _putInMemory = (primaryKeyValue: string, ent: any) => {
    if (ent['__archivedEntries']) {
      // EXP: アーカイブされているデータを個別レコードとして登録する。__archivedEntriesはすでに展開された情報が入っている前提
      ent['__archivedEntries'].forEach((ae: any) => {
        const secondaryKey = ae[this.secondaryKeyName];
        if (!this.dataCache[primaryKeyValue][secondaryKey] || this.dataCache[primaryKeyValue][secondaryKey].updatedAt <= ae.updatedAt) {
          this.dataCache[primaryKeyValue][secondaryKey] = ae;
          this.dataCache[primaryKeyValue][secondaryKey]['__isArchivedEntry__'] = true;
        }
      });
    } else {
      if (!this.dataCache[primaryKeyValue][ent[this.secondaryKeyName]] || this.dataCache[primaryKeyValue][ent[this.secondaryKeyName]].updatedAt <= ent.updatedAt) {
        this.dataCache[primaryKeyValue][ent[this.secondaryKeyName]] = JSON.parse(JSON.stringify(ent));
        if (ent['__archivedEntries']) delete this.dataCache[primaryKeyValue][ent[this.secondaryKeyName]]['__archivedEntries'];
      }
    }
  };

  /**
   * this.dataCacheからデータを削除する
   * secondaryKeyValueを指定しなければ、primaryKeyのデータ全体を削除する
   * @param primaryKeyValue {string}
   * @param secondaryKeyValue {string}
   * @protected
   */
  public deleteInMemory(primaryKeyValue: string, secondaryKeyValue?: string) {
    if (!this.dataCache[primaryKeyValue]) return;
    if (secondaryKeyValue) {
      if (this.dataCache[primaryKeyValue][secondaryKeyValue]) {
        delete this.dataCache[primaryKeyValue][secondaryKeyValue];
        this.dataCacheAccessTime[primaryKeyValue] = new Date().getTime();
      }
    } else {
      delete this.dataCache[primaryKeyValue];
      delete this.dataCacheAccessTime[primaryKeyValue];
    }
  }

  /**
   * データを取得する。なければIndexedDBからロードしてから探す。
   * @param primaryKeyValue {string}
   * @param secondaryKeyValue {string}
   */
  public async getInMemory(primaryKeyValue: string, secondaryKeyValue: string): Promise<any> {
    if (!this.dataCache[primaryKeyValue]) {
      await this.loadInMemory(primaryKeyValue);
    }
    if (!this.dataCache[primaryKeyValue]) return;
    return this.dataCache[primaryKeyValue][secondaryKeyValue];
  }

  /**
   * 与えられたprimaryKeyがすでにメモリに存在するかを確認する
   * @param primaryKeyValue {string}
   */
  public checkKeyInMemory(primaryKeyValue: string): boolean {
    return !!this.dataCache[primaryKeyValue];
  }

  /**
   * 与えられたprimaryKeyを持つ全てのレコードを返す
   * @param primaryKeyValue {string}
   */
  public getAllForKey(primaryKeyValue: string): any[] {
    if (!this.dataCache[primaryKeyValue]) return [];
    return Object.values(this.dataCache[primaryKeyValue]);
  }

  /**
   * 与えられたprimaryKeyを持つ全てのレコードをupdatedAtで昇順に並べて返す
   * @param primaryKeyValue {string}
   */
  public getSortedArray(primaryKeyValue: string): any[] {
    if (!this.dataCache[primaryKeyValue]) return [];
    return Object.values(this.dataCache[primaryKeyValue]).sort((a: any, b: any) => a.updatedAt - b.updatedAt);
  }
}

export {InMemoryCache};
